/* eslint-disable */
import { useLazyQuery, useQuery } from '@apollo/client'
import { Button, Typography } from '@mui/material'
import Checkbox from '@mui/material/Checkbox'
import CircularProgress from '@mui/material/CircularProgress'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import { DatePicker } from '@mui/x-date-pickers'
import dayjs, { Dayjs } from 'dayjs'
import React, { useState, useMemo, useCallback } from 'react'
import { Helmet } from 'react-helmet'
import { TFunction, useTranslation } from 'react-i18next'
import { makeStyles } from 'tss-react/mui'
import * as XLSX from 'xlsx'
import GraphqlError from '../../components/GraphqlError'
import { eventsQuery, eventsTypesQuery } from '../../generated/graphql'
import DH from '../../helpers/DH'
import { getAllLanguagesFromISO } from '../../helpers/Languages'
import { useBranchFilter } from '../../hooks/BranchFilterHook'
import CalendarEvents from '../../schema/CalendarEvents'
import EventsTypes from '../../schema/EventsTypes'

interface ExportEvents {
  from: Dayjs | null
  to: Dayjs | null
  exportAttendee: boolean
}

interface EventType {
  id: string
  name: string
}

interface EventTypesMap {
  [key: string]: string
}

const useStyles = makeStyles()(() => ({
  right: {
    float: 'right',
  },
  formContainer: {
    marginBottom: '2rem',
  },
  errorText: {
    color: 'red',
    marginBottom: '1rem',
  },
}))

const createEventDataWorker = (e: any, translations: any, eventTypesMap: any, langs: any) => {
  const eventDate = dayjs.unix(e.start)
  const jsDate = eventDate.toDate()
  return [
    jsDate,
    e.allDay ? '' : eventDate.format('HH:mm'),
    e.title,
    e.id_type ? eventTypesMap[e.id_type] || '' : '',
    e.isOnline ? translations.yes : translations.no,
    e.locationName || '',
    e.location || '',
    e.language?.map((l: string) => langs[l] || l).join(', ') || '',
    e.attendeeLimit?.toString() || '0',
    e.attendees?.length.toString() || '0',
    e.branch?.name || '',
    e.organizers?.map((org: any) => org.email).join(', ') || '',
  ]
}

function downloadData(
  data: eventsQuery,
  eventTypesMap: EventTypesMap,
  translations: Record<string, string>,
  fields: string[],
  groupByAttendee: boolean,
) {
  const rows = [fields]
  const langs = getAllLanguagesFromISO()
  const locale = window.navigator.language
  dayjs.locale(locale)

  // Process events in parallel using chunks
  const CHUNK_SIZE = 50
  const chunks = []

  for (let i = 0; i < data.events.length; i += CHUNK_SIZE) {
    chunks.push(data.events.slice(i, i + CHUNK_SIZE))
  }

  // Process chunks in parallel
  const processedChunks = chunks.map((chunk) => {
    return chunk.map((e) => {
      const eventData = createEventDataWorker(e, translations, eventTypesMap, langs)

      if (groupByAttendee && e.attendees?.length) {
        const attendeeRows = new Array(e.attendees.length)
        let validAttendees = 0

        for (const a of e.attendees) {
          if (a.profile) {
            attendeeRows[validAttendees++] = [
              ...eventData,
              a.profile.email,
              a.attended ? translations.yes : translations.no,
            ]
          }
        }

        return validAttendees > 0 ? attendeeRows.slice(0, validAttendees) : []
      }

      return [eventData]
    })
  })

  // Flatten the processed chunks and add to rows
  rows.push(...processedChunks.flat(2))

  const ws = XLSX.utils.aoa_to_sheet(rows, {
    cellDates: true,
    sheetStubs: false,
  })

  // Set fixed widths for all columns
  ws['!cols'] = [
    { width: 11 }, // Date (22.09.2025)
    { width: 7 }, // Time (13:00)
    { width: 45 }, // Title
    { width: 30 }, // Event type
    { width: 6 }, // Online (Yes/No)
    { width: 15 }, // Location name
    { width: 20 }, // Location
    { width: 13 }, // Language
    { width: 8 }, // Attendee limit
    { width: 20 }, // Attendee registered
    { width: 15 }, // Branch
    { width: 45 }, // Organizers
    { width: 35 }, // Attendee email (when exporting attendees)
    { width: 8 }, // Attended (Yes/No)
  ]

  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Events')

  XLSX.writeFile(wb, `event-export-${dayjs().format('YYYY-MM-DD-HH-mm')}.xlsx`, {
    bookType: 'xlsx',
    compression: true,
    cellDates: true,
  })
}

export default function EventExportScreen(): React.ReactElement {
  const { classes } = useStyles()
  const { t } = useTranslation()
  const branchFilter = useBranchFilter()

  const [state, setState] = useState<ExportEvents>(() => ({
    from: null,
    to: dayjs(),
    exportAttendee: true,
  }))

  const translations = useMemo(
    () => ({
      date: t('Date'),
      time: t('Time'),
      title: t('Title'),
      eventType: t('Event type'),
      online: t('Online'),
      locationName: t('Location name'),
      location: t('Location'),
      language: t('Language'),
      attendeeLimit: t('Attendee max limit'),
      attendeeRegistered: t('Attendee registered'),
      branch: t('Branch'),
      organizers: t('Organizers'),
      attendeeEmail: t('Attendee email'),
      attended: t('Attended'),
      yes: t('Yes'),
      no: t('No'),
    }),
    [t],
  )

  const fields = useMemo(() => {
    const baseFields = [
      translations.date,
      translations.time,
      translations.title,
      translations.eventType,
      translations.online,
      translations.locationName,
      translations.location,
      translations.language,
      translations.attendeeLimit,
      translations.attendeeRegistered,
      translations.branch,
      translations.organizers,
    ]

    if (state.exportAttendee) {
      baseFields.push(translations.attendeeEmail, translations.attended)
    }
    return baseFields
  }, [translations, state.exportAttendee])

  const { error: errorEvTypes, data: dataEvTypes } = useQuery<eventsTypesQuery>(EventsTypes.GET_EVENTS_TYPES)

  const eventTypesMap = useMemo(() => {
    if (!dataEvTypes?.eventsTypes) return {} as EventTypesMap
    return dataEvTypes.eventsTypes.reduce((acc: EventTypesMap, type) => {
      if (type.id) {
        acc[type.id] = type.name || ''
      }
      return acc
    }, {})
  }, [dataEvTypes?.eventsTypes])

  const queryVariables = useMemo(
    () => ({
      fromDate: state.from ? dayjs(state.from).startOf('day').unix() : undefined,
      toDate: state.to ? dayjs(state.to).endOf('day').unix() : undefined,
      branchFilter,
    }),
    [state.from, state.to, branchFilter],
  )

  const [loadEvents, { loading, error }] = useLazyQuery<eventsQuery>(CalendarEvents.GET_EVENTS_EXPORT, {
    variables: queryVariables,
    onCompleted: (data) => {
      try {
        downloadData(data, eventTypesMap, translations, fields, state.exportAttendee)
      } catch (err) {
        console.error('Error downloading data:', err)
      }
    },
  })

  const handleDateChange = useCallback(
    (field: keyof Pick<ExportEvents, 'from' | 'to'>) => (date: Dayjs | null) => {
      setState((prev) => ({
        ...prev,
        [field]: date,
      }))
    },
    [],
  )

  const handleExportAttendeeChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setState((prev) => ({
      ...prev,
      exportAttendee: event.target.checked,
    }))
  }, [])

  const isDateRangeValid = useMemo(() => {
    if (!state.from || !state.to) return false
    return dayjs(state.to).isAfter(state.from)
  }, [state.from, state.to])

  return (
    <div>
      <Helmet>
        <title>{t('Export events')}</title>
      </Helmet>
      <Typography variant="body1" gutterBottom>
        {t('Please select the events date range to export')}
      </Typography>
      <Grid container spacing={2} className={classes.formContainer}>
        <Grid item xs>
          <FormControl required fullWidth>
            <DatePicker
              label={t('From')}
              inputFormat={DH.getDateFormat()}
              renderInput={(params) => <TextField variant="standard" {...params} required />}
              value={state.from}
              onChange={handleDateChange('from')}
              maxDate={state.to || undefined}
            />
          </FormControl>
        </Grid>
        <Grid item xs>
          <FormControl required fullWidth>
            <DatePicker
              label={t('To')}
              inputFormat={DH.getDateFormat()}
              renderInput={(params) => <TextField variant="standard" {...params} required />}
              value={state.to}
              onChange={handleDateChange('to')}
              minDate={state.from || undefined}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={<Checkbox checked={state.exportAttendee} onChange={handleExportAttendeeChange} />}
            label={t('Export attendees')}
          />
        </Grid>
      </Grid>
      {(error || errorEvTypes) && (
        <Typography className={classes.errorText}>
          {t('An error occurred while loading data. Please try again.')}
        </Typography>
      )}
      <Button
        startIcon={loading && <CircularProgress size={22} />}
        className={classes.right}
        onClick={() => loadEvents()}
        color="primary"
        disabled={!isDateRangeValid || loading}
        variant="contained"
      >
        {t('Export')}
      </Button>
    </div>
  )
}
